import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useBeneficiarios,
  useBuscarColaboradores,
  useCambiarDetalleSemana,
  useDetalleSemana,
  useProveedores,
  useSemanas,
} from '@/hooks/useFlujo';
import { cliente } from '@/lib/cliente';
import type {
  BancoAsignado,
  Beneficiario,
  DetalleCaso,
  Moneda,
  TipoDestinatario,
  TotalesDetalle,
} from '@adelante/shared';
import { formatearColones, formatearDolares } from '@adelante/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const formatear = (monto: number, moneda: Moneda) =>
  moneda === 'C' ? formatearColones(monto) : formatearDolares(monto);

/** Minúsculas y sin acentos, para que "Josue" encuentre a "Josué". */
const sinAcentos = (texto: string) => texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

const aNumero = (texto: string) => Number(texto.replace(/,/g, '').trim());

/** BAC, BN o "—" (a mano, sin archivo). Volver a pulsar la opción activa la quita. */
function SelectorBancoCaso({
  valor,
  deshabilitado,
  onElegir,
}: {
  valor: BancoAsignado | null;
  deshabilitado: boolean;
  onElegir: (b: BancoAsignado | null) => void;
}) {
  const opciones: Array<{ v: BancoAsignado; t: string; titulo: string }> = [
    { v: 'BAC', t: 'BAC', titulo: 'Sale en el archivo del BAC' },
    { v: 'BN', t: 'BN', titulo: 'Sale en el archivo del Banco Nacional' },
    { v: 'MANUAL', t: '—', titulo: 'Se paga a mano, no entra a ningún archivo' },
  ];
  return (
    <fieldset
      aria-label="Banco de pago"
      className="m-0 inline-flex overflow-hidden rounded-md border p-0 text-[11px] font-medium"
    >
      {opciones.map((o) => (
        <button
          key={o.v}
          type="button"
          aria-pressed={valor === o.v}
          title={o.titulo}
          disabled={deshabilitado}
          onClick={() => onElegir(valor === o.v ? null : o.v)}
          className={`px-1.5 py-1 leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            valor === o.v
              ? o.v === 'BAC'
                ? 'bg-red-600 text-white'
                : o.v === 'BN'
                  ? 'bg-sky-700 text-white'
                  : 'bg-zinc-300 text-zinc-800'
              : 'bg-transparent text-muted-foreground hover:bg-zinc-100'
          }`}
        >
          {o.t}
        </button>
      ))}
    </fieldset>
  );
}

/** A quién se le paga y qué tiene para poder pagarle: viene de un proveedor o de un beneficiario. */
interface Destino {
  nombre: string;
  tieneCedula: boolean;
  cuentas: Array<{ moneda: Moneda }>;
  /** Pantalla donde completar lo que falta (solo proveedores; los beneficiarios se completan aquí mismo). */
  enlace: string | null;
}

/** Dice si el destinatario tiene cédula y cuenta IBAN en la moneda del caso (lo que hace falta para pagarle). */
function EstadoCuenta({
  destino,
  moneda,
  compacto = false,
}: {
  destino: Destino | null;
  moneda: Moneda;
  compacto?: boolean;
}) {
  if (!destino) return null;
  const hayCuenta = destino.cuentas.some((c) => c.moneda === moneda);
  const faltas = [
    !destino.tieneCedula && 'cédula',
    !hayCuenta && `cuenta IBAN en ${moneda === 'C' ? 'colones' : 'dólares'}`,
  ].filter(Boolean);
  if (faltas.length === 0) {
    return compacto ? null : (
      <p className="text-xs text-muted-foreground">Cédula y cuenta IBAN en orden para pagarle.</p>
    );
  }
  return (
    <p className="text-xs text-amber-700">
      Falta {faltas.join(' y ')}
      {destino.enlace ? (
        <>
          : se completa en{' '}
          <Link className="underline" to={destino.enlace}>
            Proveedores
          </Link>
        </>
      ) : null}
      .
    </p>
  );
}

/**
 * Elige o crea el beneficiario de un caso (colaborador o cliente) y captura su cuenta IBAN. Los
 * colaboradores se buscan en dbo.Colaborador (la cédula la toma el servidor y no viaja al navegador);
 * los clientes se crean con nombre y cédula.
 */
function SelectorBeneficiario({
  tipo,
  beneficiarios,
  seleccionadoId,
  moneda,
  etiqueta,
  categoriaId,
  pedirCuenta,
  onSeleccion,
}: {
  tipo: Exclude<TipoDestinatario, 'PROVEEDOR'>;
  beneficiarios: Beneficiario[];
  seleccionadoId: number | null;
  moneda: Moneda;
  /** Cómo se llama la persona en esta categoría (Vendedor, Colaborador, Cliente). */
  etiqueta: string;
  categoriaId: number | null;
  /** false cuando la cuenta de la persona no se usa (el pago sale a otra empresa, como en Comisiones). */
  pedirCuenta: boolean;
  onSeleccion: (b: { id: number; nombre: string } | null) => void;
}) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [trayendo, setTrayendo] = useState(false);
  const { data: encontrados } = useBuscarColaboradores(
    tipo === 'COLABORADOR' ? texto : '',
    categoriaId,
  );
  const [nuevo, setNuevo] = useState(false);
  const [nombre, setNombre] = useState('');
  const [cedula, setCedula] = useState('');
  const [iban, setIban] = useState('');
  const seleccionado = beneficiarios.find((b) => b.id === seleccionadoId) ?? null;
  const lista = beneficiarios.filter(
    (b) =>
      b.tipo === tipo &&
      (!filtroCliente.trim() || sinAcentos(b.nombre).includes(sinAcentos(filtroCliente.trim()))),
  );

  const traerClientes = async () => {
    setTrayendo(true);
    const res = await cliente.beneficiarios.sincronizarClientes();
    setTrayendo(false);
    if (!res.data) {
      toast.error(res.error?.mensaje ?? 'No se pudieron traer los clientes');
      return;
    }
    toast.success(
      res.data.creados > 0
        ? `${res.data.creados} cliente(s) nuevo(s) traído(s)`
        : 'No hay clientes nuevos que traer',
    );
    await qc.invalidateQueries({ queryKey: ['beneficiarios'] });
  };

  const registrar = async (datos: Parameters<typeof cliente.beneficiarios.crear>[0]) => {
    const res = await cliente.beneficiarios.crear(datos);
    if (!res.data) {
      toast.error(res.error?.mensaje ?? 'No se pudo registrar');
      return;
    }
    await qc.invalidateQueries({ queryKey: ['beneficiarios'] });
    return res.data.id;
  };

  const elegirColaborador = async (colaboradorId: number, nombreColaborador: string) => {
    const id = await registrar({ tipo: 'COLABORADOR', colaboradorId });
    if (id) {
      onSeleccion({ id, nombre: nombreColaborador });
      setTexto('');
    }
  };

  const crearCliente = async () => {
    if (!nombre.trim()) {
      toast.error('Indicá el nombre del cliente');
      return;
    }
    const id = await registrar({ tipo: 'CLIENTE', nombre: nombre.trim(), cedula: cedula.trim() });
    if (id) {
      onSeleccion({ id, nombre: nombre.trim() });
      setNuevo(false);
      setNombre('');
      setCedula('');
    }
  };

  const guardarCuenta = async () => {
    if (!seleccionado) return;
    const res = await cliente.beneficiarios.agregarCuenta(seleccionado.id, { moneda, iban });
    if (res.error) {
      toast.error(res.error.mensaje);
      return;
    }
    setIban('');
    toast.success('Cuenta guardada');
    await qc.invalidateQueries({ queryKey: ['beneficiarios'] });
  };

  const hayCuenta = seleccionado?.cuentas.some((c) => c.moneda === moneda) ?? false;

  return (
    <div className="space-y-2">
      <Label>{etiqueta}</Label>
      {seleccionado ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{seleccionado.nombre}</span>
          {seleccionado.cedulaTerminaEn && (
            <span className="text-xs text-muted-foreground">
              cédula ···{seleccionado.cedulaTerminaEn}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={() => onSeleccion(null)}>
            Cambiar
          </Button>
        </div>
      ) : tipo === 'COLABORADOR' ? (
        <div className="space-y-1">
          <Input
            placeholder="Buscar por nombre o cédula (mínimo 2 letras)"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          {encontrados && encontrados.length > 0 && (
            <ul className="max-h-48 overflow-auto rounded-md border text-sm">
              {encontrados.map((c) => (
                <li key={c.colaboradorId}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted"
                    onClick={() => elegirColaborador(c.colaboradorId, c.nombre)}
                  >
                    <span>
                      {c.nombre}
                      {!c.activo && <span className="ml-2 text-xs text-amber-700">inactivo</span>}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ···{c.cedulaTerminaEn ?? '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {encontrados && encontrados.length === 0 && texto.trim().length >= 2 && (
            <p className="text-xs text-muted-foreground">Sin coincidencias en colaboradores.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Input
            placeholder="Filtrar clientes por nombre"
            value={filtroCliente}
            onChange={(e) => setFiltroCliente(e.target.value)}
          />
          <select
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            value=""
            onChange={(e) => {
              const b = lista.find((x) => x.id === Number(e.target.value));
              if (b) onSeleccion({ id: b.id, nombre: b.nombre });
            }}
          >
            <option value="">— Elegir cliente registrado ({lista.length}) —</option>
            {lista.map((b) => (
              <option key={b.id} value={b.id}>
                {b.nombre}
              </option>
            ))}
          </select>
          {nuevo ? (
            <div className="grid gap-2 sm:grid-cols-[1fr_12rem_auto]">
              <Input
                placeholder="Nombre del cliente"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
              <Input
                placeholder="Cédula"
                value={cedula}
                onChange={(e) => setCedula(e.target.value)}
              />
              <Button onClick={crearCliente}>Crear</Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setNuevo(true)}>
                Nuevo cliente
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={trayendo}
                title="Trae los clientes activos nuevos de AdelanteDB (también se hace solo cada noche)"
                onClick={traerClientes}
              >
                {trayendo ? 'Trayendo…' : 'Traer clientes nuevos'}
              </Button>
            </div>
          )}
        </div>
      )}
      {pedirCuenta && seleccionado && !seleccionado.tieneCedula && (
        <p className="text-xs text-amber-700">Falta la cédula de este beneficiario.</p>
      )}
      {pedirCuenta && seleccionado && !hayCuenta && (
        <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
          <div className="space-y-1">
            <Label className="text-xs text-amber-700">
              Falta la cuenta IBAN en {moneda === 'C' ? 'colones' : 'dólares'}
            </Label>
            <Input
              placeholder="CR + 20 dígitos"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && guardarCuenta()}
            />
          </div>
          <Button variant="outline" onClick={guardarCuenta} disabled={iban.trim().length < 15}>
            Guardar cuenta
          </Button>
        </div>
      )}
      {pedirCuenta && seleccionado && hayCuenta && (
        <p className="text-xs text-muted-foreground">Cédula y cuenta IBAN en orden para pagarle.</p>
      )}
    </div>
  );
}

/** Fila de un caso: el monto a pagar se edita en la misma fila y se guarda al salir del campo. */
function FilaCaso({
  caso,
  destino,
  semanaId,
  onEditar,
  onQuitar,
}: {
  caso: DetalleCaso;
  destino: Destino | null;
  semanaId: number;
  onEditar: () => void;
  onQuitar: () => void;
}) {
  const { guardarPago } = useCambiarDetalleSemana();
  const [texto, setTexto] = useState(caso.aPagar === null ? '' : String(caso.aPagar));

  const guardar = async (montoAPagar: number, pagado: boolean, banco?: BancoAsignado | null) => {
    const res = await guardarPago.mutateAsync({
      id: caso.id,
      datos: { semanaId, montoAPagar, pagado, ...(banco !== undefined ? { banco } : {}) },
    });
    if (res.error) {
      toast.error(res.error.mensaje);
      setTexto(caso.aPagar === null ? '' : String(caso.aPagar));
    }
  };

  const alSalir = () => {
    const valor = texto.trim() === '' ? 0 : aNumero(texto);
    if (Number.isNaN(valor) || valor < 0) {
      toast.error('Monto inválido');
      setTexto(caso.aPagar === null ? '' : String(caso.aPagar));
      return;
    }
    if (valor === (caso.aPagar ?? 0)) return;
    guardar(valor, false);
  };

  return (
    <tr className="border-b last:border-0">
      <td className="py-2">
        <div className="font-medium">{caso.beneficiario}</div>
        {caso.descripcion && (
          <div className="text-xs text-muted-foreground">{caso.descripcion}</div>
        )}
        {caso.plan && (
          <div className="text-xs text-muted-foreground">
            {caso.plan.periodicidad === 'MENSUAL'
              ? `Mensual · ${formatear(caso.plan.montoCuota, caso.moneda)} × ${caso.plan.numCuotas} · cuota ${Math.min(caso.cuotasPagadas + 1, caso.plan.numCuotas)} de ${caso.plan.numCuotas}`
              : `Pago único · ${formatear(caso.plan.montoCuota, caso.moneda)}`}
          </div>
        )}
        {caso.aPagar === null && (caso.cuotaSugerida ?? 0) > 0 && !caso.pagado && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => guardar(caso.cuotaSugerida ?? 0, false)}
          >
            Usar cuota de {formatear(caso.cuotaSugerida ?? 0, caso.moneda)}
          </Button>
        )}
        <EstadoCuenta destino={destino} moneda={caso.moneda} compacto />
      </td>
      <td className="py-2 text-right tabular-nums">{formatear(caso.adeudado, caso.moneda)}</td>
      <td className="w-64 py-2 pl-3">
        <div className="flex items-center gap-1.5">
          {!caso.pagado &&
            caso.adeudado > 0 &&
            [
              { texto: '33%', factor: 1 / 3 },
              { texto: '50%', factor: 0.5 },
              { texto: 'Total', factor: 1 },
            ].map(({ texto: etiqueta, factor }) => {
              const monto = Math.round(caso.adeudado * factor * 100) / 100;
              return monto !== caso.aPagar ? (
                <Button
                  key={etiqueta}
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0 px-2 text-xs"
                  title={`Pagar ${etiqueta === 'Total' ? 'todo lo adeudado' : `el ${etiqueta} de lo adeudado`}`}
                  disabled={guardarPago.isPending}
                  onClick={() => guardar(monto, false)}
                >
                  {etiqueta}
                </Button>
              ) : null;
            })}
          <Input
            inputMode="decimal"
            className="h-8 text-right"
            value={texto}
            disabled={caso.pagado}
            onChange={(e) => setTexto(e.target.value)}
            onBlur={alSalir}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          />
        </div>
      </td>
      <td className="py-2 pl-3 text-center">
        {destino ? (
          <SelectorBancoCaso
            valor={caso.banco}
            deshabilitado={caso.pagado || (caso.aPagar ?? 0) <= 0 || guardarPago.isPending}
            onElegir={(b) => guardar(caso.aPagar ?? 0, caso.pagado, b)}
          />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-2 pl-3 text-right tabular-nums">{formatear(caso.saldo, caso.moneda)}</td>
      <td className="py-2 text-center">
        <input
          type="checkbox"
          className="h-4 w-4"
          title="Confirmar pagado: el saldo pasa a la semana siguiente"
          checked={caso.pagado}
          disabled={(caso.aPagar ?? 0) <= 0 || guardarPago.isPending}
          onChange={(e) => guardar(caso.aPagar ?? 0, e.target.checked)}
        />
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        <Button variant="ghost" size="icon" title="Editar caso" onClick={onEditar}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" title="Eliminar caso" onClick={onQuitar}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

function FilaTotal({
  etiqueta,
  totales,
  moneda,
}: {
  etiqueta: string;
  totales: TotalesDetalle;
  moneda: Moneda;
}) {
  if (totales.adeudado === 0 && totales.aPagar === 0) return null;
  return (
    <tr className="border-t font-semibold">
      <td className="pt-3">{etiqueta}</td>
      <td className="pt-3 text-right tabular-nums">{formatear(totales.adeudado, moneda)}</td>
      <td className="pt-3 pl-3 text-right tabular-nums">{formatear(totales.aPagar, moneda)}</td>
      <td />
      <td className="pt-3 pl-3 text-right tabular-nums">{formatear(totales.saldo, moneda)}</td>
      <td colSpan={3} />
    </tr>
  );
}

/**
 * Subsección de detalle de una categoría de Pagos Semanales (ej. Liquidaciones laborales). Cada caso es
 * una persona con un total adeudado: se digita lo que se paga cada semana, al confirmarlo el saldo se
 * arrastra a la siguiente, y al llegar a cero el caso pasa a Archivadas. La hoja semanal solo muestra
 * el total por moneda.
 */
export function DetalleCategoriaPantalla() {
  const { categoriaId: categoriaParam } = useParams();
  const categoriaId = Number(categoriaParam) || null;
  const [params, setParams] = useSearchParams();
  const { data: semanas } = useSemanas();
  const semanaId = Number(params.get('semana')) || semanas?.[0]?.id || null;

  const { data, isLoading, error } = useDetalleSemana(semanaId, categoriaId);
  const { crear, actualizar, eliminar } = useCambiarDetalleSemana();

  const [vista, setVista] = useState<'vigentes' | 'archivadas'>('vigentes');
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [beneficiario, setBeneficiario] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [moneda, setMoneda] = useState<Moneda>('C');
  const [monto, setMonto] = useState('');
  const [proveedorId, setProveedorId] = useState<number | null>(null);
  const hoy = new Date().toLocaleDateString('en-CA');
  const [periodicidad, setPeriodicidad] = useState<'UNICA' | 'MENSUAL'>('MENSUAL');
  const [cuota, setCuota] = useState('');
  const [numCuotas, setNumCuotas] = useState('12');
  const [fechaInicio, setFechaInicio] = useState(hoy);
  const { data: proveedores } = useProveedores();
  const proveedorDe = (id: number | null) => proveedores?.find((p) => p.id === id) ?? null;
  const { data: beneficiarios } = useBeneficiarios();
  const [beneficiarioId, setBeneficiarioId] = useState<number | null>(null);
  const tipo: TipoDestinatario = data?.tipoDestinatario ?? 'PROVEEDOR';
  const pagador = data?.pagador ?? null;
  const destinoDe = (provId: number | null, benId: number | null): Destino | null => {
    const p = proveedorDe(pagador ? pagador.proveedorId : provId);
    if (p) {
      return {
        nombre: p.nombre,
        tieneCedula: Boolean(p.cedula),
        cuentas: p.cuentas,
        enlace: '/flujo-semanal/proveedores',
      };
    }
    const b = beneficiarios?.find((x) => x.id === benId);
    if (b)
      return { nombre: b.nombre, tieneCedula: b.tieneCedula, cuentas: b.cuentas, enlace: null };
    return null;
  };

  const limpiar = () => {
    setEditandoId(null);
    setBeneficiario('');
    setDescripcion('');
    setMonto('');
    setProveedorId(null);
    setBeneficiarioId(null);
    setPeriodicidad('MENSUAL');
    setCuota('');
    setNumCuotas('12');
    setFechaInicio(hoy);
  };

  const editar = (c: DetalleCaso) => {
    setEditandoId(c.id);
    setBeneficiario(c.beneficiario);
    setDescripcion(c.descripcion ?? '');
    setMoneda(c.moneda);
    setMonto(String(c.montoTotal));
    setProveedorId(c.proveedorId);
    setBeneficiarioId(c.beneficiarioId);
    setPeriodicidad(c.plan?.periodicidad ?? 'MENSUAL');
    setCuota(c.plan ? String(c.plan.montoCuota) : '');
    setNumCuotas(c.plan ? String(c.plan.numCuotas) : '12');
    setFechaInicio(c.plan?.fechaInicio ?? hoy);
  };

  const cuotaNum = aNumero(cuota);
  const cuotasNum = periodicidad === 'UNICA' ? 1 : Math.trunc(aNumero(numCuotas));
  const totalPlan =
    cuotaNum > 0 && cuotasNum >= 1 ? Math.round(cuotaNum * cuotasNum * 100) / 100 : 0;

  const guardar = async () => {
    const valor = data?.conPlan ? totalPlan : aNumero(monto);
    if (!semanaId || !categoriaId) return;
    if (!beneficiario.trim()) {
      toast.error('Indicá el nombre');
      return;
    }
    if (!(valor > 0)) {
      toast.error(
        data?.conPlan
          ? 'Indicá el monto de la cuota y el número de cuotas'
          : 'Indicá el monto total adeudado',
      );
      return;
    }
    if (data?.conPlan && !fechaInicio) {
      toast.error('Indicá la fecha del primer pago');
      return;
    }
    if (data?.requiereProveedor && tipo === 'PROVEEDOR' && !pagador && !proveedorId) {
      toast.error('Elegí el proveedor: en esta categoría es obligatorio');
      return;
    }
    if (data?.requiereProveedor && tipo !== 'PROVEEDOR' && !beneficiarioId) {
      toast.error(`Elegí el ${tipo === 'COLABORADOR' ? 'colaborador' : 'cliente'}: es obligatorio`);
      return;
    }
    const datos = {
      beneficiario: beneficiario.trim(),
      descripcion: descripcion.trim() || null,
      moneda,
      montoTotal: valor,
      proveedorId: tipo === 'PROVEEDOR' && !pagador ? proveedorId : null,
      beneficiarioId: tipo === 'PROVEEDOR' ? null : beneficiarioId,
      plan: data?.conPlan
        ? { periodicidad, montoCuota: cuotaNum, numCuotas: cuotasNum, fechaInicio }
        : null,
    };
    const res = editandoId
      ? await actualizar.mutateAsync({ id: editandoId, datos })
      : await crear.mutateAsync({ ...datos, semanaId, categoriaId });
    if (res.error) {
      toast.error(res.error.mensaje);
      return;
    }
    limpiar();
  };

  const quitar = async (c: DetalleCaso) => {
    if (!window.confirm(`¿Eliminar el caso de ${c.beneficiario} con todos sus pagos?`)) return;
    const res = await eliminar.mutateAsync(c.id);
    if (res.error) toast.error(res.error.mensaje);
    else if (editandoId === c.id) limpiar();
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="outline" size="icon" title="Volver a Pagos Semanales">
          <Link to="/flujo-semanal">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-xl font-semibold">{data?.categoriaNombre ?? 'Detalle'}</h1>
        {semanas && semanaId && (
          <Select
            value={String(semanaId)}
            onValueChange={(v) => {
              setParams({ semana: v });
              limpiar();
            }}
          >
            <SelectTrigger className="ml-auto w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {semanas.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  Semana {s.numeroSemana} · {s.anio}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {editandoId ? 'Editar caso' : 'Agregar caso (aparece desde esta semana)'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pagador && (
            <p className="rounded-md border bg-muted/40 p-2 text-sm">
              Estos casos son el detalle por persona. El banco recibe <strong>un solo pago</strong>{' '}
              con el total a <strong>{pagador.nombre}</strong>.
              <EstadoCuenta destino={destinoDe(null, null)} moneda={moneda} />
            </p>
          )}
          {!pagador && tipo === 'PROVEEDOR' ? (
            <div className="space-y-1">
              <Label htmlFor="det-proveedor">
                Proveedor{data?.requiereProveedor ? '' : ' (opcional)'}
              </Label>
              <select
                id="det-proveedor"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                value={proveedorId ?? ''}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  setProveedorId(id);
                  const p = proveedorDe(id);
                  if (p && !beneficiario.trim()) setBeneficiario(p.nombre);
                }}
              >
                <option value="">— Sin proveedor —</option>
                {proveedores?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                    {p.cedula ? ` · ${p.cedula}` : ''}
                  </option>
                ))}
              </select>
              <EstadoCuenta destino={destinoDe(proveedorId, null)} moneda={moneda} />
            </div>
          ) : tipo === 'PROVEEDOR' ? null : (
            <SelectorBeneficiario
              tipo={tipo}
              etiqueta={pagador ? 'Vendedor' : tipo === 'COLABORADOR' ? 'Colaborador' : 'Cliente'}
              categoriaId={categoriaId}
              pedirCuenta={!pagador}
              beneficiarios={beneficiarios ?? []}
              seleccionadoId={beneficiarioId}
              moneda={moneda}
              onSeleccion={(b) => {
                setBeneficiarioId(b?.id ?? null);
                if (b && !beneficiario.trim()) setBeneficiario(b.nombre);
              }}
            />
          )}
          {data?.conPlan && (
            <div className="grid gap-3 sm:grid-cols-4 sm:items-end">
              <div className="space-y-1">
                <Label>Tipo de pago</Label>
                <Select
                  value={periodicidad}
                  onValueChange={(v) => setPeriodicidad(v as 'UNICA' | 'MENSUAL')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MENSUAL">Mensual (varias cuotas)</SelectItem>
                    <SelectItem value="UNICA">Pago único</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="det-cuota">
                  {periodicidad === 'UNICA' ? 'Monto del pago' : 'Monto de la cuota'}
                </Label>
                <Input
                  id="det-cuota"
                  inputMode="decimal"
                  className="text-right"
                  value={cuota}
                  onChange={(e) => setCuota(e.target.value)}
                />
              </div>
              {periodicidad === 'MENSUAL' && (
                <div className="space-y-1">
                  <Label htmlFor="det-cuotas">Número de cuotas</Label>
                  <Input
                    id="det-cuotas"
                    inputMode="numeric"
                    className="text-right"
                    value={numCuotas}
                    onChange={(e) => setNumCuotas(e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="det-inicio">
                  {periodicidad === 'UNICA' ? 'Fecha del pago' : 'Primera cuota'}
                </Label>
                <Input
                  id="det-inicio"
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_7rem_10rem_auto] sm:items-end">
            <div className="space-y-1">
              <Label htmlFor="det-beneficiario">Nombre</Label>
              <Input
                id="det-beneficiario"
                value={beneficiario}
                onChange={(e) => setBeneficiario(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="det-descripcion">{pagador ? 'Casa o venta' : 'Descripción'}</Label>
              <Input
                id="det-descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Moneda</Label>
              <Select value={moneda} onValueChange={(v) => setMoneda(v as Moneda)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="C">₡ Colones</SelectItem>
                  <SelectItem value="D">$ Dólares</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="det-monto">
                {data?.conPlan ? 'Total del plan' : 'Monto total adeudado'}
              </Label>
              <Input
                id="det-monto"
                inputMode="decimal"
                className="text-right"
                value={data?.conPlan ? (totalPlan > 0 ? formatear(totalPlan, moneda) : '') : monto}
                readOnly={Boolean(data?.conPlan)}
                onChange={(e) => setMonto(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && guardar()}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={guardar} disabled={crear.isPending || actualizar.isPending}>
                {editandoId ? 'Guardar' : 'Agregar'}
              </Button>
              {editandoId && (
                <Button variant="outline" onClick={limpiar}>
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant={vista === 'vigentes' ? 'default' : 'outline'}
          onClick={() => setVista('vigentes')}
        >
          Vigentes{data ? ` (${data.casos.length})` : ''}
        </Button>
        <Button
          size="sm"
          variant={vista === 'archivadas' ? 'default' : 'outline'}
          onClick={() => setVista('archivadas')}
        >
          Archivadas{data ? ` (${data.archivados.length})` : ''}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
          {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

          {data && vista === 'vigentes' && data.casos.length === 0 && (
            <p className="text-sm text-muted-foreground">No hay casos vigentes en esta semana.</p>
          )}
          {data && vista === 'vigentes' && data.casos.length > 0 && semanaId && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">Nombre</th>
                  <th className="py-2 text-right font-medium">Adeudado</th>
                  <th className="py-2 pl-3 text-right font-medium">A pagar</th>
                  <th className="py-2 pl-3 text-center font-medium">Banco</th>
                  <th className="py-2 pl-3 text-right font-medium">Saldo</th>
                  <th className="py-2 text-center font-medium">Pagado</th>
                  <th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {data.casos.map((c) => (
                  <FilaCaso
                    key={`${c.id}-${semanaId}-${c.aPagar}-${c.pagado}`}
                    caso={c}
                    destino={destinoDe(c.proveedorId, c.beneficiarioId)}
                    semanaId={semanaId}
                    onEditar={() => editar(c)}
                    onQuitar={() => quitar(c)}
                  />
                ))}
              </tbody>
              <tfoot>
                <FilaTotal etiqueta="Total colones" totales={data.totalColones} moneda="C" />
                <FilaTotal etiqueta="Total dólares" totales={data.totalDolares} moneda="D" />
              </tfoot>
            </table>
          )}

          {data && vista === 'archivadas' && data.archivados.length === 0 && (
            <p className="text-sm text-muted-foreground">Todavía no hay casos cancelados.</p>
          )}
          {data && vista === 'archivadas' && data.archivados.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">Nombre</th>
                  <th className="py-2 text-right font-medium">Total pagado</th>
                  <th className="py-2 pl-3 font-medium">Cancelado en</th>
                </tr>
              </thead>
              <tbody>
                {data.archivados.map((a) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2">
                      <div className="font-medium">{a.beneficiario}</div>
                      {a.descripcion && (
                        <div className="text-xs text-muted-foreground">{a.descripcion}</div>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {formatear(a.montoTotal, a.moneda)}
                    </td>
                    <td className="py-2 pl-3">{a.canceladoEn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
